import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Map frontend routes/pages to module names (these should match ModuleName in Moduleslookup)
const MODULE_MAP = {
  '/projects': 'Projects',
  '/tasks': 'Tasks',
  '/time-transactions': 'Time & Transactions',
  '/break-leisure': 'Break & Leisure',
  '/users': 'Users',
  '/service-management': 'Service Management',
};

export const usePermissions = () => {
  const { user } = useAuth();
  const [accessibleModules, setAccessibleModules] = useState([]);
  const [permissions, setPermissions] = useState({}); // { moduleId: [permissionIds] }
  const [loading, setLoading] = useState(true);
  const [moduleIdMap, setModuleIdMap] = useState({}); // { moduleName: moduleId }
  const [actionIdMap, setActionIdMap] = useState({}); // { actionName: actionId }

  // Fetch accessible modules
  const fetchModules = useCallback(async () => {
    if (!user) {
      setAccessibleModules([]);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/user/modules`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch modules');
      }

      const modules = await res.json();
      console.log('[fetchModules] Fetched accessible modules for user:', user.email, user.serviceid);
      console.log('[fetchModules] Modules received:', modules);
      console.log('[fetchModules] Modules array length:', Array.isArray(modules) ? modules.length : 'NOT AN ARRAY');
      const modulesArray = Array.isArray(modules) ? modules : [];
      setAccessibleModules(modulesArray);
      
      // Create module name to ID map
      const nameToIdMap = {};
      modulesArray.forEach(module => {
        if (module.ModuleName && module.ID) {
          nameToIdMap[module.ModuleName] = module.ID;
        }
      });
      console.log('[fetchModules] ModuleIdMap created:', nameToIdMap);
      setModuleIdMap(nameToIdMap);
    } catch (err) {
      console.error('Fetch modules error:', err);
      setAccessibleModules([]);
      setModuleIdMap({});
    }
  }, [user]);

  // Fetch all permissions
  const fetchPermissions = useCallback(async () => {
    if (!user) {
      console.log('[fetchPermissions] No user, clearing permissions');
      setPermissions({});
      setLoading(false);
      return;
    }

    console.log('[fetchPermissions] Starting fetch for user:', user.email, user.serviceid);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('[fetchPermissions] No token found');
        setPermissions({});
        return;
      }
      
      console.log('[fetchPermissions] Fetching from:', `${process.env.REACT_APP_API_URL}/service-management/user/permissions`);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/service-management/user/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('[fetchPermissions] Response status:', res.status, res.statusText);
      if (!res.ok) {
        const errorText = await res.text();
        console.error('[fetchPermissions] Response not OK:', errorText);
        throw new Error(`Failed to fetch permissions: ${res.status} ${errorText}`);
      }

      const perms = await res.json();
      console.log('[fetchPermissions] Permissions received:', perms);
      console.log('[fetchPermissions] Permissions type:', typeof perms, 'Is array:', Array.isArray(perms));
      console.log('[fetchPermissions] Permissions keys:', Object.keys(perms || {}));
      
      // Ensure moduleId keys are numbers for consistent lookup
      const normalizedPerms = {};
      Object.keys(perms || {}).forEach(key => {
        const numKey = parseInt(key);
        if (!isNaN(numKey)) {
          normalizedPerms[numKey] = perms[key];
        } else {
          normalizedPerms[key] = perms[key];
        }
      });
      console.log('[fetchPermissions] Normalized permissions:', normalizedPerms);
      setPermissions(normalizedPerms);
      
      // Fetch all actions to create action name to ID map
      console.log('[fetchPermissions] Fetching actions from:', `${process.env.REACT_APP_API_URL}/service-management/actions/all`);
      try {
        const actionRes = await fetch(`${process.env.REACT_APP_API_URL}/service-management/actions/all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('[fetchPermissions] Actions response status:', actionRes.status);
        if (actionRes.ok) {
          const actions = await actionRes.json();
          console.log('[fetchPermissions] Actions received:', actions);
          console.log('[fetchPermissions] Actions is array:', Array.isArray(actions));
          const nameToIdMap = {};
          if (Array.isArray(actions)) {
            actions.forEach(action => {
              if (action.ActionName && action.Id) {
                nameToIdMap[action.ActionName] = action.Id;
              }
            });
          }
          console.log('[fetchPermissions] Action ID Map:', nameToIdMap);
          setActionIdMap(nameToIdMap);
        } else {
          const errorText = await actionRes.text();
          console.error('[fetchPermissions] Actions fetch failed:', errorText);
        }
      } catch (err) {
        console.error('[fetchPermissions] Fetch actions error:', err);
      }
    } catch (err) {
      console.error('[fetchPermissions] Fetch permissions error:', err);
      console.error('[fetchPermissions] Error stack:', err.stack);
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Check if user can access a module by route path
  const canAccessModule = useCallback((routePath) => {
    if (!user) return false;
    
    // Admin can access everything
    if (user.role === 'admin') return true;
    
    // CMO should respect permission settings, so check accessibleModules below
    
    const moduleName = MODULE_MAP[routePath];
    if (!moduleName) {
      // Unknown routes default to accessible (for backward compatibility)
      return true;
    }
    
    // If no serviceid, allow access (backward compatibility)
    if (!user.serviceid) {
      return true;
    }
    
    // If still loading and we have no modules yet, wait
    // But if we already have some modules, use them (prevents blocking on slow network)
    if (loading && accessibleModules.length === 0) {
      console.log(`[canAccessModule] Still loading and no modules yet for ${routePath}, returning false`);
      return false;
    }
    
    // Check if module exists in accessible modules
    console.log(`[canAccessModule] Checking ${routePath} (${moduleName}) against:`, accessibleModules.map(m => m.ModuleName));
    const hasAccess = accessibleModules.some(module => {
      const dbModuleName = String(module.ModuleName || '').trim();
      const mappedModuleName = String(moduleName).trim();
      const matches = dbModuleName.toLowerCase() === mappedModuleName.toLowerCase();
      if (matches) {
        console.log(`[canAccessModule] Match found: "${dbModuleName}" === "${mappedModuleName}"`);
      }
      return matches;
    });
    
    console.log(`[canAccessModule] Result for ${routePath}:`, hasAccess);
    return hasAccess;
  }, [user, accessibleModules, loading]);

  // Check if user can perform an action in a module by action name (e.g., "Add", "Edit", "Delete", "View")
  const canPerformAction = useCallback((moduleId, actionName) => {
    if (!user || !moduleId || !actionName) {
      console.log(`[canPerformAction] Missing params: user=${!!user}, moduleId=${moduleId}, actionName=${actionName}`);
      return false;
    }
    
    // Admin can perform all actions
    if (user.role === 'admin') return true;
    
    // Handle both string and number moduleId keys - try all variations
    const moduleIdNum = parseInt(moduleId);
    const moduleIdStr = String(moduleId);
    const modulePermissions = permissions[moduleId] || 
                              permissions[moduleIdNum] || 
                              permissions[moduleIdStr] || 
                              permissions[String(moduleIdNum)] || 
                              [];
    const actionId = actionIdMap[actionName];
    
    console.log(`[canPerformAction] Checking: moduleId=${moduleId} (num: ${moduleIdNum}, str: "${moduleIdStr}"), actionName=${actionName}, actionId=${actionId}`);
    console.log(`[canPerformAction] Module permissions for moduleId ${moduleId}:`, modulePermissions);
    console.log(`[canPerformAction] All permissions keys:`, Object.keys(permissions));
    console.log(`[canPerformAction] All permissions:`, permissions);
    console.log(`[canPerformAction] ActionIdMap:`, actionIdMap);
    
    if (!actionId) {
      console.log(`[canPerformAction] Action ID not found for "${actionName}"`);
      return false;
    }
    
    // Check if permission exists (handle both string and number IDs)
    const actionIdNum = parseInt(actionId);
    const hasPermission = modulePermissions.some(permId => 
      permId === actionId || 
      permId === actionIdNum || 
      parseInt(permId) === actionIdNum ||
      String(permId) === String(actionId)
    );
    
    console.log(`[canPerformAction] Result: ${hasPermission} (checking if ${actionId}/${actionIdNum} in [${modulePermissions.join(', ')}])`);
    return hasPermission;
  }, [user, permissions, actionIdMap]);
  
  // Check if user can perform an action by route path and action name
  const canPerformActionByRoute = useCallback((routePath, actionName) => {
    if (!user || !routePath || !actionName) {
      console.log(`[canPerformActionByRoute] Missing params: user=${!!user}, routePath=${routePath}, actionName=${actionName}`);
      return false;
    }
    
    if (user.role === 'admin') return true;
    
    const moduleName = MODULE_MAP[routePath];
    if (!moduleName) {
      console.log(`[canPerformActionByRoute] Module name not found for route: ${routePath}`);
      return false;
    }
    
    const moduleId = moduleIdMap[moduleName];
    console.log(`[canPerformActionByRoute] Checking ${routePath} -> ${moduleName} (ID: ${moduleId}) for action "${actionName}"`);
    console.log(`[canPerformActionByRoute] ModuleIdMap:`, moduleIdMap);
    
    if (!moduleId) {
      console.log(`[canPerformActionByRoute] Module ID not found for "${moduleName}"`);
      return false;
    }
    
    return canPerformAction(moduleId, actionName);
  }, [user, moduleIdMap, canPerformAction]);

  // Get permission IDs for a module
  const getModulePermissions = useCallback((moduleId) => {
    if (!user || !moduleId) return [];
    
    if (user.role === 'admin') {
      // Return all possible permissions (we'll need to fetch this)
      return [];
    }
    
    return permissions[moduleId] || [];
  }, [user, permissions]);

  // Check if user has a specific permission ID in a module
  const hasPermission = useCallback((moduleId, permissionId) => {
    if (!user || !moduleId || !permissionId) return false;
    
    if (user.role === 'admin') return true;
    
    const modulePermissions = permissions[moduleId] || [];
    return modulePermissions.includes(permissionId);
  }, [user, permissions]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      Promise.all([fetchModules(), fetchPermissions()]).finally(() => {
        setLoading(false);
      });
    } else {
      setAccessibleModules([]);
      setPermissions({});
      setLoading(false);
    }
  }, [user, fetchModules, fetchPermissions]);

  return {
    accessibleModules,
    permissions,
    loading,
    canAccessModule,
    canPerformAction,
    canPerformActionByRoute,
    getModulePermissions,
    hasPermission,
    moduleIdMap,
    actionIdMap,
  };
};

